<script setup lang="ts">
import { ElMessageBox } from 'element-plus'
import { computed, h } from 'vue'
import { RepairRequestStatus } from '@/types/rc/repair-requests'

const model = defineModel()
const props = defineProps<{ repairRequestNumber: string; status: string }>()
const emit = defineEmits(['statusAction'])

const actions = computed<
  | {
      positive?: { label: string; nextStatus: string }
      negative?: { label: string; nextStatus: string }
      neutral?: { label: string; nextStatus: string }
    }
  | undefined
>(() => {
  if (props.status === RepairRequestStatus.UnderReview) {
    return {
      positive: {
        label: 'Approve',
        nextStatus: RepairRequestStatus.Approved,
      },
      negative: {
        label: 'Deny',
        nextStatus: RepairRequestStatus.Denied,
      },
    }
  } else if (props.status === RepairRequestStatus.Approved) {
    return {
      positive: {
        label: 'Complete',
        nextStatus: RepairRequestStatus.Complete,
      },
      negative: {
        label: 'Cancel',
        nextStatus: RepairRequestStatus.Cancelled,
      },
    }
  } else if (props.status === RepairRequestStatus.Complete) {
    return {
      positive: {
        label: 'Re-open',
        nextStatus: RepairRequestStatus.Approved,
      },
    }
  } else if (props.status === RepairRequestStatus.Denied) {
    return {
      neutral: {
        label: 'Review',
        nextStatus: RepairRequestStatus.UnderReview,
      },
    }
  } else {
    return undefined
  }
})

/**
 * Handles status action click.
 * Shows confirmation dialog. If confirmed, emits new status to parent component.
 */
const handleStatusAction = async (e: Event, newStatus: string, type: 'positive' | 'negative' | 'neutral') => {
  e.stopPropagation()

  try {
    const action = actions.value![type]!.label
    let confirmButtonClass: string | undefined
    if (type === 'negative') {
      confirmButtonClass = 'el-button--danger'
    } else if (type === 'neutral') {
      confirmButtonClass = 'el-button--warning'
    } else {
      confirmButtonClass = 'el-button--success'
    }

    await ElMessageBox({
      title: `${action} Repair Request`,
      message: h('div', null, [
        h(
          'p',
          null,
          `Are you sure you want to ${action.toLowerCase()} Repair Request #${props.repairRequestNumber.slice(-4)}?`
        ),
      ]),
      showClose: false,
      showCancelButton: true,
      cancelButtonText: 'Back',
      showConfirmButton: true,
      confirmButtonText: action,
      confirmButtonClass,
    })
    model.value = type
    emit('statusAction', newStatus)
  } catch {
    // ElMessageBox throws an Error on cancel click (in this case, do nothing).
  }
}
</script>

<template>
  <div v-if="actions" class="status-actions">
    <el-button
      v-if="actions.positive"
      type="success"
      :loading="model === 'positive'"
      :disabled="!!model"
      @click="handleStatusAction($event, actions.positive.nextStatus, 'positive')"
      >{{ actions.positive.label }}</el-button
    >
    <el-button
      v-if="actions.neutral"
      type="warning"
      :loading="model === 'neutral'"
      :disabled="!!model"
      @click="handleStatusAction($event, actions.neutral.nextStatus, 'neutral')"
      >{{ actions.neutral.label }}</el-button
    >
    <el-button
      v-if="actions.negative"
      type="danger"
      :loading="model === 'negative'"
      :disabled="!!model"
      @click="handleStatusAction($event, actions.negative.nextStatus, 'negative')"
      >{{ actions.negative.label }}</el-button
    >
  </div>
</template>

<style scoped>
/* Override Element Plus button space rule */
.el-button + .el-button {
  margin-left: 0;
}

.status-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-spacing-xs);
}
</style>
